import { Component, EventEmitter, Inject, Input, Output, inject } from '@angular/core';
import { User } from '@app/core/models';
import { LS_PERMANENT_PREFIX } from '@app/core/models/ui.model';
import { NeoUserTenantInfo } from '@app/core/models/genesis-user-profile.model';
import { AuthChangeEvent, AuthenticationService } from '@app/services/authentication.service';
import {
  MSAL_GUARD_CONFIG,
  MsalGuardConfiguration,
  MsalService,
} from '@azure/msal-angular';
import { InteractionType } from '@azure/msal-browser';
import { combineLatest, map, Observable, Subject, takeUntil } from 'rxjs';
import { GenesisUserService } from '@app/core/services/genesis-user.service';
import { NeoApiConfigService } from '@app/core/services/neo-api-config.service';
import { AppStateService } from '@app/services/app-state.service';
import { CrossTabLogoutService } from '@app/core/services/cross-tab-logout.service';
import { NavigationService } from '@app/services/navigation.service';

@Component({
  selector: 'neo-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  standalone: false
})
export class HeaderComponent {
  @Input() pinned = false;
  @Input() showMenuButton = true;

  @Output() toggleSideNav = new EventEmitter<null>();

  user: User | null = null;
  currentTenantKey$: Observable<string | null>;
  isGenesisMode: boolean = false;

  /** Active tenant info matching the currently selected tenant key, or null. */
  currentTenantInfo$: Observable<NeoUserTenantInfo | null>;

  /** All tenants on the profile where isActive === true. */
  activeTenants$: Observable<NeoUserTenantInfo[]>;

  /** True when the user has more than one active tenant and the switcher should render. */
  hasMultipleActiveTenants$: Observable<boolean>;

  private destroy$ = new Subject<void>();
  private readonly genesisUserService = inject(GenesisUserService);
  private readonly apiConfigService = inject(NeoApiConfigService);
  private readonly appStateService = inject(AppStateService);
  private readonly logoutService = inject(CrossTabLogoutService);
  private readonly navigationService = inject(NavigationService);

  constructor(
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private authService: MsalService,
    private authenticationService: AuthenticationService,
  ) {
    this.user = this.authenticationService.getActiveUser();
    this.currentTenantKey$ = this.genesisUserService.currentTenantKey$;
    this.isGenesisMode = this.apiConfigService.isGenesisMode();

    this.currentTenantInfo$ = combineLatest([
      this.genesisUserService.userProfile$,
      this.genesisUserService.currentTenantKey$
    ]).pipe(
      map(([profile, tenantKey]) => {
        if (!profile || !tenantKey) return null;
        return profile.tenants.find(t => t.tenantKey === tenantKey) ?? null;
      })
    );

    this.activeTenants$ = this.genesisUserService.userProfile$.pipe(
      map(profile => profile ? profile.tenants.filter(t => t.isActive) : [])
    );

    this.hasMultipleActiveTenants$ = this.activeTenants$.pipe(
      map(tenants => tenants.length > 1)
    );

    this.subscribeAuthEvents();
  }

  /**
   * Switch to a different active tenant via the hybrid re-init flow (D001).
   * Invoked from the header switcher menu.
   */
  public switchTenant(tenantKey: string, currentTenantKey: string | null): void {
    if (tenantKey === currentTenantKey) return;
    void this.appStateService.switchTenant(tenantKey);
  }

  // NOTE: Use this method only to completely clear localStorage (excluding permanent items prefixed with LS_PERMANENT_PREFIX)
  static clearLocalStorage(): void {
    for (const key in localStorage) {
      // eslint-disable-next-line no-prototype-builtins
      if (localStorage.hasOwnProperty(key) && !key.startsWith(LS_PERMANENT_PREFIX)) {
        localStorage.removeItem(key);
      }
    }
  }

  logout(): void {
    // Signal cross-tab logout BEFORE clearing local storage so peer tabs can
    // react before MSAL tears down the shared session. The peer tabs handle
    // the signal at the root component level — see AppComponent.
    this.logoutService.signalLogout();

    // NEO-4214: Logout is a deliberate fresh start, so release both redirect
    // breakers. Their counters live in sessionStorage, which clearLocalStorage()
    // below never touches — so without this a tripped breaker survives the
    // logout and eats into the next sign-in's allowance.
    this.authenticationService.clearLibraryRedirectBreaker();
    this.authenticationService.clearRedirectBreaker();

    HeaderComponent.clearLocalStorage();

    // Drop the per-tab persisted returnUrl so the post-logout login lands at
    // root rather than the page the user was on before signing out.
    this.navigationService.clearPersistedReturnUrl();

    // Mark this tab as having self-logged-out so the post-B2C boot renders
    // the overlay instead of auto-firing loginRedirect — see AppStateService.
    this.navigationService.markSelfLoggedOut();

    if (this.msalGuardConfig.interactionType === InteractionType.Popup) {
      this.authService.logoutPopup({
        mainWindowRedirectUri: '/',
      });
    } else {
      this.authService.logoutRedirect();
    }
  }

  private subscribeAuthEvents(): void {
    this.authenticationService.changedEvent$
      .pipe(takeUntil(this.destroy$))
      .subscribe((authChangeEvent: AuthChangeEvent) => {
        this.user = authChangeEvent.user;
      });
  }
}
